import { useEffect, useState } from "react";
import { Palette, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageStyle, STYLE_KEYS } from "@/lib/pageStyle";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props { currentStyle: PageStyle; onApply: (style: PageStyle) => void; disabled?: boolean }

export const themeToStyle = (t: any): PageStyle => Object.fromEntries(STYLE_KEYS.map((k) => [k, t[k]]).filter(([, v]) => v));

export function ThemePicker({ currentStyle, onApply, disabled }: Props) {
  const { user } = useAuth() as any;
  const [themes, setThemes] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const load = () => api.get("/themes").then((r) => setThemes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = (id: string) => { const t = themes.find((x) => x.id === id); if (t) { onApply(themeToStyle(t)); toast.success(`Theme “${t.name}” applied to all pages`); } };
  const save = async () => {
    if (!name.trim()) return toast.error("Give the theme a name");
    try { await api.post("/themes", { name: name.trim(), ...currentStyle }); toast.success("Theme saved to the library"); setName(""); setSaving(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-w-0 overflow-hidden rounded-md border border-dashed border-gray-300 p-2.5" data-testid="theme-picker">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700"><Palette size={12} className="text-blue-700" />Starlites themes</div>
      <div className="mt-2 flex min-w-0 flex-wrap gap-2">
        <select data-testid="theme-select" disabled={disabled} className="h-8 min-w-0 flex-1 basis-[160px] rounded-md border border-gray-300 bg-white px-2 text-xs" value="" onChange={(e) => e.target.value && apply(e.target.value)}>
          <option value="">Apply a theme to all pages…</option>
          {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {user?.role === "admin" && !saving && <Button type="button" size="sm" variant="outline" disabled={disabled} className="h-8 shrink-0 whitespace-nowrap text-xs" data-testid="save-theme-btn" onClick={() => setSaving(true)}><Save size={12} className="mr-1" />Save as theme</Button>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {themes.map((t) => (
          <button key={t.id} type="button" disabled={disabled} data-testid={`theme-swatch-${t.id}`} title={t.name} onClick={() => apply(t.id)} className="flex max-w-full items-center gap-1 rounded-full border border-gray-200 px-2 py-0.5 text-[10px] text-gray-700 hover:border-blue-400">
            <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: t.bg_color }} /><span className="h-3 w-3 shrink-0 rounded-full" style={{ background: t.accent_color }} /><span className="truncate">{t.name}</span>
          </button>
        ))}
      </div>
      {saving && (
        <div className="mt-2 flex gap-2" data-testid="save-theme-form">
          <div className="flex-1"><Label className="sr-only">Theme name</Label><Input data-testid="theme-name-input" className="h-8 text-xs" placeholder="Theme name, e.g. Midnight Glow" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} /></div>
          <Button type="button" size="sm" className="h-8 bg-blue-700 text-xs hover:bg-blue-800" data-testid="theme-save-confirm-btn" onClick={save}>Save</Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" data-testid="theme-save-cancel-btn" onClick={() => setSaving(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}
